// AICA Export Utilities
// Export data to CSV and PDF
// Bloomberg Terminal style: Professional, complete, accessible

import { logError, logWarning, logInfo } from '@/lib/logger';
import { formatDateTime } from '@/lib/utils';
import i18n from '@/i18n/config';

/**
 * Export data to CSV file
 * @param data - Array of objects to export
 * @param filename - Output filename (without extension)
 * @param columns - Optional column mapping (key -> display name)
 * @param preamble - Optional `label,value` lines written before the table — what the file counts
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  columns?: Partial<Record<keyof T, string>>,
  preamble?: readonly (readonly [string, string])[]
): void {
  if (!data.length) {
    logWarning('No data to export', { module: 'Export', function: 'exportToCSV' });
    return;
  }

  const firstRow = data[0];
  if (!firstRow) {
    logWarning('No data to export', { module: 'Export', function: 'exportToCSV' });
    return;
  }

  // Get headers
  const keys = Object.keys(firstRow) as (keyof T)[];
  const headers = columns
    ? keys.map((key) => columns[key] || String(key))
    : keys.map(String);

  // Build CSV content
  const csvRows: string[] = [];

  if (preamble?.length) {
    for (const [label, value] of preamble) {
      csvRows.push(`${escapeCSVValue(label)},${escapeCSVValue(value)}`);
    }
    csvRows.push('');
  }

  // Add header row
  csvRows.push(headers.map(escapeCSVValue).join(','));

  // Add data rows
  for (const row of data) {
    const values = keys.map((key) => {
      const value = row[key];
      return escapeCSVValue(value);
    });
    csvRows.push(values.join(','));
  }

  const csvContent = csvRows.join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
}

/**
 * Escape text for safe interpolation into an HTML document.
 */
function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Neutralize spreadsheet formula injection (CSV injection).
 *
 * A cell beginning with =, +, -, @, TAB or CR is evaluated as a formula by
 * Excel / Google Sheets on open. Prefix such cells with a single quote so the
 * spreadsheet treats them as text — but leave legitimate negative numbers
 * (e.g. "-5") untouched, since "-" is both a formula trigger and a valid sign.
 */
function neutralizeFormula(value: string): string {
  if (value === '') return value;
  const first = value[0]!;
  if (
    first === '=' ||
    first === '+' ||
    first === '@' ||
    first === '\t' ||
    first === '\r'
  ) {
    return `'${value}`;
  }
  // Leading '-' is a formula trigger, but a plain negative number is legitimate.
  if (first === '-' && !Number.isFinite(Number(value))) {
    return `'${value}`;
  }
  return value;
}

/**
 * Escape CSV value (neutralize formula injection, then handle quotes/commas/newlines)
 */
export function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = neutralizeFormula(String(value));

  // If value contains comma, quote, or newline, wrap in quotes
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n')
  ) {
    // Escape quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Export chart/data to PDF
 * Uses browser print functionality with custom styling
 *
 * @param element - HTML element to export (chart container)
 * @param filename - Output filename (without extension)
 * @param options - Export options
 */
export interface ExportPDFOptions {
  /**
   * Page title
   */
  title?: string;
  /**
   * Page orientation
   */
  orientation?: 'portrait' | 'landscape';
  /**
   * Include timestamp
   */
  includeTimestamp?: boolean;
  /**
   * Custom CSS for print
   */
  customCSS?: string;
}

export function exportToPDF(
  element: HTMLElement,
  _filename: string,
  options: ExportPDFOptions = {}
): void {
  const t = i18n.t.bind(i18n);
  const {
    title = t('exports.pdf.title'),
    orientation = 'landscape',
    includeTimestamp = true,
    customCSS = '',
  } = options;

  // Escape the caller-supplied title before interpolating it into the print
  // HTML — otherwise a title carrying markup breaks out of <title>/<h1> (XSS in
  // the print document). The cloned element's outerHTML is browser-serialized
  // from the live (already-escaped) React DOM, so it does not need re-escaping.
  const safeTitle = escapeHtmlText(title);

  // Create a print window
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert(t('exports.pdf.popupBlocked'));
    return;
  }

  // Clone the element
  const clonedElement = element.cloneNode(true) as HTMLElement;

  // Build HTML content
  const timestamp = includeTimestamp
    ? `<p style="font-size: 12px; color: #666; margin: 10px 0;">${t(
        'exports.pdf.generated',
        { date: formatDateTime(new Date()) }
      )}</p>`
    : '';

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${safeTitle}</title>
        <style>
          @page {
            size: ${orientation === 'landscape' ? 'A4 landscape' : 'A4 portrait'};
            margin: 1cm;
          }

          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
          }

          h1 {
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 10px;
            color: #000;
          }

          .content {
            margin-top: 20px;
          }

          /* Chart styling */
          .recharts-surface {
            max-width: 100% !important;
            height: auto !important;
          }

          /* Hide interactive elements */
          button {
            display: none !important;
          }

          ${customCSS}
        </style>
      </head>
      <body>
        <h1>${safeTitle}</h1>
        ${timestamp}
        <div class="content">
          ${clonedElement.outerHTML}
        </div>
        <script>
          window.onload = function() {
            window.print();
            // Close window after print dialog closes (with delay)
            setTimeout(() => window.close(), 100);
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

/**
 * Export data as JSON file
 */
export function exportToJSON<T>(data: T, filename: string, pretty: boolean = true): void {
  const jsonContent = pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);

  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  downloadBlob(blob, `${filename}.json`);
}

/**
 * Download a blob as a file
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Export chart as PNG image
 * Uses html2canvas library (needs to be installed separately)
 */
export async function exportChartAsImage(
  element: HTMLElement,
  filename: string,
  format: 'png' | 'jpeg' = 'png'
): Promise<void> {
  const t = i18n.t.bind(i18n);
  try {
    // Dynamically import html2canvas (optional dependency)
    const module = await import('html2canvas');
    const html2canvas = module.default;

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher resolution
    });

    canvas.toBlob((blob: Blob | null) => {
      if (blob) {
        downloadBlob(blob, `${filename}.${format}`);
      }
    }, `image/${format}`);
  } catch (error) {
    logError(
      'Failed to export chart as image',
      { module: 'Export', function: 'exportChartAsImage', format },
      error instanceof Error ? error : undefined
    );
    alert(t('exports.image.missingLibrary'));
  }
}

/**
 * Copy data to clipboard as CSV
 */
export async function copyToClipboard<T extends Record<string, unknown>>(
  data: T[],
  columns?: Partial<Record<keyof T, string>>
): Promise<void> {
  if (!data.length) {
    return;
  }

  const firstRow = data[0];
  if (!firstRow) {
    return;
  }

  // Build CSV content
  const keys = Object.keys(firstRow) as (keyof T)[];
  const headers = columns
    ? keys.map((key) => columns[key] || String(key))
    : keys.map(String);

  const csvRows: string[] = [headers.join('\t')];

  for (const row of data) {
    const values = keys.map((key) => String(row[key] ?? ''));
    csvRows.push(values.join('\t'));
  }

  const text = csvRows.join('\n');

  try {
    await navigator.clipboard.writeText(text);
    logInfo('Data copied to clipboard', {
      module: 'Export',
      function: 'copyToClipboard',
      rowCount: data.length,
    });
  } catch (error) {
    logError(
      'Failed to copy to clipboard',
      { module: 'Export', function: 'copyToClipboard' },
      error instanceof Error ? error : undefined
    );
  }
}
